import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  FormGroup,
  Label,
  Input,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap'
import {
  FaArrowLeft,
  FaCheckCircle,
  FaSearch,
  FaTruck,
  FaUser,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaTimes,
  FaCheck,
  FaPlus,
  FaTrash
} from 'react-icons/fa'
import { Formik, Field, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import { motion } from 'framer-motion'
import { nominateTruck, fetchRequestDetails } from '../../store/slices/transportRequestSlice'
import { searchDrivers } from '../../store/slices/driverSlice'
import api from '../../services/api'
import { GLOBAL_SEARCH_TRUCK, GET_BALANCE_QUANTITY } from '../../helpers/url_helper'
import './TransportRequest.scss'

const formatDate = date => {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const NominateTruckPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  
  const { selectedRequest, loading: requestLoading } = useSelector(state => state.transportRequest)
  const { drivers = [], loading: driversLoading } = useSelector(state => state.driver)

  // Truck search state
  const [truckSearchQuery, setTruckSearchQuery] = useState('')
  const [truckSearching, setTruckSearching] = useState(false)
  const [truckSearchResult, setTruckSearchResult] = useState(null)
  const [truckSearchError, setTruckSearchError] = useState('')
  const [selectedTruck, setSelectedTruck] = useState(null)
  const [selectedDriver, setSelectedDriver] = useState(null)
  const [driverModalOpen, setDriverModalOpen] = useState(false)
  const [driverSearchName, setDriverSearchName] = useState('')
  const [driverSearchLicense, setDriverSearchLicense] = useState('')
  const [driverSearchActive, setDriverSearchActive] = useState('')
  const [queuedNominations, setQueuedNominations] = useState([])
  const [queueError, setQueueError] = useState('')
  const [balanceQuantity, setBalanceQuantity] = useState(null)
  const [balanceLoading, setBalanceLoading] = useState(false)
  const [balanceError, setBalanceError] = useState('')

  useEffect(() => {
    dispatch(fetchRequestDetails(id))
    dispatch(searchDrivers({ page: 1, limit: 100 }))
  }, [dispatch, id])

  const openDriverModal = () => {
    setDriverModalOpen(true)
    dispatch(searchDrivers({ page: 1, limit: 100 }))
  }

  const closeDriverModal = () => {
    setDriverModalOpen(false)
  }

  const searchDriverList = async () => {
    const params = {}
    if (driverSearchName && driverSearchName.trim()) params.name = driverSearchName.trim()
    if (driverSearchLicense && driverSearchLicense.trim()) params.licenseNumber = driverSearchLicense.trim()
    if (driverSearchActive !== '') params.isActive = driverSearchActive === 'true'
    params.page = 1
    params.limit = 100
    await dispatch(searchDrivers(params))
  }

  const resetDriverSearch = () => {
    setDriverSearchName('')
    setDriverSearchLicense('')
    setDriverSearchActive('')
    dispatch(searchDrivers({ page: 1, limit: 100 }))
  }

  const pickDriver = (driver, setFieldValue) => {
    const driverId = driver.id || driver.driver_id
    setSelectedDriver(driver)
    setFieldValue('driver_id', driverId)
    setDriverModalOpen(false)
  }

  useEffect(() => {
    let isActive = true

    const fetchBalanceQuantity = async () => {
      setBalanceLoading(true)
      setBalanceError('')
      try {
        const response = await api.get(`${GET_BALANCE_QUANTITY}/${id}/balance-quantity`)
        if (!isActive) return
        setBalanceQuantity(response.data?.data || null)
      } catch (error) {
        if (!isActive) return
        setBalanceError(error.response?.data?.error || 'Failed to fetch quantity balance.')
      } finally {
        if (isActive) setBalanceLoading(false)
      }
    }

    fetchBalanceQuantity()

    return () => {
      isActive = false
    }
  }, [id])

  const handleSearchTruck = async () => {
    if (!truckSearchQuery.trim()) return
    setTruckSearching(true)
    setTruckSearchResult(null)
    setTruckSearchError('')
    setSelectedTruck(null)
    try {
      const response = await api.get(`${GLOBAL_SEARCH_TRUCK}/${truckSearchQuery.trim().toUpperCase()}`)
      const truck = response.data?.data || response.data?.truck || response.data
      if (truck && (truck.truck_id || truck.id)) {
        setTruckSearchResult(truck)
      } else {
        setTruckSearchError('No truck found with this registration number.')
      }
    } catch (error) {
      setTruckSearchError(error.response?.data?.error || 'Truck not found in the system.')
    } finally {
      setTruckSearching(false)
    }
  }

  const handleDriverSelect = (driverId, setFieldValue) => {
    const driver = drivers.find(d => String(d.driver_id || d.id || d._id) === String(driverId))
    setSelectedDriver(driver)
    setFieldValue('driver_id', driverId)
  }

  const remainingQuantity = parseFloat(balanceQuantity?.remaining_quantity ?? selectedRequest?.total_quantity ?? 0) || 0

  const validationSchema = Yup.object().shape({
    truck_id: Yup.string().nullable(),
    driver_id: Yup.string().nullable(),
    loading_capacity: Yup.number()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(0.01, 'Loading capacity must be greater than 0')
      .test(
        'max-remaining-quantity',
        'Loading capacity cannot exceed remaining quantity.',
        value => {
          if (value == null) return true
          return value <= remainingQuantity
        }
      ),
  })

  const buildDraftNomination = values => {
    const truckId = parseInt(values.truck_id)
    const driverId = parseInt(values.driver_id)
    const loadingCapacity = parseFloat(values.loading_capacity)

    if (!truckId || !driverId || !loadingCapacity) return null

    return {
      truck_id: truckId,
      driver_id: driverId,
      loading_capacity: loadingCapacity,
      truck_number: selectedTruck?.truck_number || selectedTruck?.truckNumber || values.truck_id,
      driver_name: selectedDriver?.name || 'Selected Driver',
    }
  }

  const handleAddNomination = (values, setFieldValue) => {
    setQueueError('')
    if (!values.truck_id || !selectedTruck) {
      setQueueError('Please search and select a truck first.')
      return
    }
    if (!values.driver_id || !selectedDriver) {
      setQueueError('Please select a driver for this truck.')
      return
    }
    if (!values.loading_capacity || parseFloat(values.loading_capacity) <= 0) {
      setQueueError('Please enter loading capacity greater than 0.')
      return
    }

    if (remainingQuantity > 0 && parseFloat(values.loading_capacity) > remainingQuantity) {
      setQueueError(`Loading capacity cannot exceed remaining quantity (${remainingQuantity}).`)
      return
    }

    const nextItem = buildDraftNomination(values)
    if (!nextItem) {
      setQueueError('Unable to add nomination. Please verify all fields.')
      return
    }

    const hasDuplicateTruck = queuedNominations.some(item => item.truck_id === nextItem.truck_id)
    if (hasDuplicateTruck) {
      setQueueError('This truck is already added to nomination list.')
      return
    }

    setQueuedNominations(prev => [...prev, nextItem])
    setSelectedTruck(null)
    setSelectedDriver(null)
    setTruckSearchQuery('')
    setTruckSearchResult(null)
    setFieldValue('truck_id', '')
    setFieldValue('driver_id', '')
    setFieldValue('loading_capacity', '')
  }

  const removeQueuedNomination = truckId => {
    setQueuedNominations(prev => prev.filter(item => item.truck_id !== truckId))
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const draftNomination = buildDraftNomination(values)
      const nominationItems = draftNomination
        ? [...queuedNominations, draftNomination]
        : [...queuedNominations]

      if (nominationItems.length === 0) {
        setQueueError('Add at least one truck nomination before submitting.')
        setSubmitting(false)
        return
      }

      const isOverLimit = nominationItems.some(item => item.loading_capacity > remainingQuantity)
      if (isOverLimit) {
        setQueueError(`One or more loading capacity values exceed remaining quantity (${remainingQuantity}).`)
        setSubmitting(false)
        return
      }

      const truckData = nominationItems.length === 1
        ? {
            truck_id: nominationItems[0].truck_id,
            driver_id: nominationItems[0].driver_id,
            loading_capacity: nominationItems[0].loading_capacity,
          }
        : {
            trucks: nominationItems.map(item => ({
              truck_id: item.truck_id,
              driver_id: item.driver_id,
              loading_capacity: item.loading_capacity,
            }))
          }
      
      // Get the accepted proposal ID from the request data
      const acceptedProposal = selectedRequest?.my_proposals?.find(p => p.status === 'accepted')
      const proposalId = acceptedProposal?.id

      await dispatch(nominateTruck({ requestId: id, proposalId, truckData })).unwrap()
      setQueuedNominations([])
      setQueueError('')
      navigate(`/transport-request/${id}`)
    } catch (error) {
      console.error('Nomination error:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  }

  if (requestLoading || !selectedRequest) {
    return (
      <div className="trl-loading">
        <Spinner animation="border" size="sm" style={{ color: 'rgb(20,35,115)' }} />
        <span>Loading nomination workspace…</span>
      </div>
    )
  }

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="management-page nominate-truck-page ntp-page"
    >
      <Container fluid>
        <motion.div variants={itemVariants} className="ntp-header">
          <Button
            color="link"
            className="ntp-back-btn"
            onClick={() => navigate(`/transport-request/${id}`)}
          >
            <FaArrowLeft className="me-2" />
            Back to Request
          </Button>

          <div className="ntp-header-main">
            <div className="ntp-header-icon"><FaTruck /></div>
            <div>
              <h2 className="ntp-title">Nominate Vehicle & Driver</h2>
              <p className="ntp-subtitle">
                Finalize movement resources for {selectedRequest.request_number}
                {selectedRequest.contract_number ? ` • Contract ${selectedRequest.contract_number}` : ''}
              </p>
            </div>
          </div>
        </motion.div>

        <Row className="g-3 mb-3">
          <Col md={4}>
            <div className="ntp-kpi">
              <span className="ntp-kpi-label">Route</span>
              <span className="ntp-kpi-value">{selectedRequest.origin} to {selectedRequest.destination}</span>
            </div>
          </Col>
          <Col md={4}>
            <div className="ntp-kpi">
              <span className="ntp-kpi-label">Dispatch Date</span>
              <span className="ntp-kpi-value"><FaCalendarAlt className="me-2" />{formatDate(selectedRequest.loading_date)}</span>
            </div>
          </Col>
          <Col md={4}>
            <div className="ntp-kpi">
              <span className="ntp-kpi-label">Declared Quantity</span>
              <span className="ntp-kpi-value">{selectedRequest.total_quantity} {selectedRequest.quantity_unit}</span>
            </div>
          </Col>
        </Row>

        <div className="ntp-balance-panel mb-3">
          <div className="ntp-balance-head">
            <div>
              <h6 className="ntp-balance-title mb-1">Balance Quantity</h6>
              <p className="ntp-balance-subtitle mb-0">Live nomination progress for this request</p>
            </div>
            {balanceLoading ? <Spinner size="sm" /> : null}
          </div>

          {balanceError ? (
            <div className="ntp-error mt-2">{balanceError}</div>
          ) : (
            <Row className="g-3 mt-1">
              <Col md={3} xs={6}>
                <div className="ntp-balance-card">
                  <span className="ntp-balance-lbl">Nominated</span>
                  <span className="ntp-balance-val">{balanceQuantity?.nominated_quantity ?? '—'} {balanceQuantity?.quantity_unit || selectedRequest.quantity_unit || ''}</span>
                </div>
              </Col>
              <Col md={3} xs={6}>
                <div className="ntp-balance-card">
                  <span className="ntp-balance-lbl">Remaining</span>
                  <span className="ntp-balance-val">{balanceQuantity?.remaining_quantity ?? '—'} {balanceQuantity?.quantity_unit || selectedRequest.quantity_unit || ''}</span>
                </div>
              </Col>
              <Col md={3} xs={6}>
                <div className="ntp-balance-card">
                  <span className="ntp-balance-lbl">Nominations</span>
                  <span className="ntp-balance-val">{balanceQuantity?.nominations_count ?? '—'}</span>
                </div>
              </Col>
              <Col md={3} xs={6}>
                <div className="ntp-balance-card">
                  <span className="ntp-balance-lbl">Quantity Met</span>
                  <span className={`ntp-balance-val ${balanceQuantity?.is_quantity_met ? 'ntp-balance-val--green' : 'ntp-balance-val--amber'}`}>
                    {balanceQuantity?.is_quantity_met ? 'Yes' : 'No'}
                  </span>
                </div>
              </Col>
            </Row>
          )}
        </div>

        <p className="ntp-instruction">Choose an approved vehicle first, then assign the best available driver.</p>

        <Row className="g-4">
          <Col lg={8}>
            <Formik
              initialValues={{ truck_id: '', driver_id: '', loading_capacity: '' }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, isSubmitting, setFieldValue, errors, touched }) => (
                <FormikForm>
                  <motion.div variants={itemVariants}>
                    <Card className="ntp-panel mb-4">
                      <CardBody className="p-4">
                        <h5 className="ntp-section-title mb-4 d-flex align-items-center">
                          <FaTruck className="me-2" />
                          Vehicle Assignment
                        </h5>

                        <FormGroup className="mb-4">
                          <Label className="ntp-field-label">Search Vehicle (Registration Number)</Label>
                          <div className="ntp-search-row">
                            <Input
                              className="ntp-input"
                              placeholder="e.g. MH12AB1234"
                              value={truckSearchQuery}
                              onChange={e => setTruckSearchQuery(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleSearchTruck())}
                              disabled={!!selectedTruck}
                            />
                            {!selectedTruck ? (
                              <Button className="ntp-btn ntp-btn--primary px-4" type="button" onClick={handleSearchTruck} disabled={truckSearching || !truckSearchQuery.trim()}>
                                {truckSearching ? <Spinner size="sm" /> : <><FaSearch className="me-2" />Search</>}
                              </Button>
                            ) : (
                              <Button className="ntp-btn ntp-btn--secondary px-4" type="button" onClick={() => {
                                setSelectedTruck(null)
                                setTruckSearchResult(null)
                                setTruckSearchQuery('')
                                setFieldValue('truck_id', '')
                              }}>
                                <FaTimes className="me-2" />Change
                              </Button>
                            )}
                          </div>
                          {truckSearchError && <div className="ntp-error mt-2">{truckSearchError}</div>}
                          {touched.truck_id && errors.truck_id && !truckSearchError && <div className="ntp-error mt-2">{errors.truck_id}</div>}
                        </FormGroup>

                        {truckSearchResult && !selectedTruck && (
                          <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="ntp-select-card mb-4"
                            onClick={() => {
                              const truckId = truckSearchResult.truck_id || truckSearchResult.id
                              setSelectedTruck(truckSearchResult)
                              setTruckSearchResult(null)
                              setFieldValue('truck_id', String(truckId))
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
                              <h6 className="mb-0 ntp-card-title">
                                <FaTruck className="me-2" />
                                {truckSearchResult.truck_number || truckSearchResult.truckNumber}
                              </h6>
                              <Badge className="ntp-badge">Select Vehicle</Badge>
                            </div>
                            <Row className="g-3">
                              <Col md={4} xs={6}>
                                <small className="ntp-mini-lbl d-block">Type</small>
                                <span className="ntp-mini-val">{truckSearchResult.truck_type || '—'}</span>
                              </Col>
                              <Col md={4} xs={6}>
                                <small className="ntp-mini-lbl d-block">Capacity</small>
                                <span className="ntp-mini-val">{truckSearchResult.capacity || truckSearchResult.truck_capacity || '—'}</span>
                              </Col>
                            </Row>
                          </motion.div>
                        )}

                        {selectedTruck && (
                          <motion.div 
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="ntp-selected-card mb-4"
                          >
                            <h6 className="mb-3 ntp-selected-title d-flex align-items-center">
                              <FaCheckCircle className="me-2" />
                              Vehicle Selected
                            </h6>
                            <Row className="g-3">
                              <Col md={4} xs={6}>
                                <small className="ntp-mini-lbl d-block">Number</small>
                                <span className="ntp-mini-val">{selectedTruck.truck_number || selectedTruck.truckNumber}</span>
                              </Col>
                              <Col md={4} xs={6}>
                                <small className="ntp-mini-lbl d-block">Type</small>
                                <span className="ntp-mini-val">{selectedTruck.truck_type || '—'}</span>
                              </Col>
                              <Col md={4} xs={6}>
                                <small className="ntp-mini-lbl d-block">Capacity</small>
                                <span className="ntp-mini-val">{selectedTruck.capacity || selectedTruck.truck_capacity || '—'}</span>
                              </Col>
                            </Row>
                          </motion.div>
                        )}

                        <h5 className="ntp-section-title mt-5 mb-4 d-flex align-items-center">
                          <FaUser className="me-2" />
                          Driver Assignment
                        </h5>

                        <FormGroup className="mb-0">
                          <Label className="ntp-field-label">Select Registered Driver</Label>
                          <Input
                            className="ntp-input"
                            placeholder="Click to search driver"
                            value={selectedDriver ? `${selectedDriver.name} (${selectedDriver.license_number || selectedDriver.drivingLicenseNumber})` : ''}
                            readOnly
                            onClick={openDriverModal}
                          />
                          <Field type="hidden" name="driver_id" />
                          {touched.driver_id && errors.driver_id && (
                            <div className="ntp-error mt-2">{errors.driver_id}</div>
                          )}
                        </FormGroup>

                        <Modal isOpen={driverModalOpen} toggle={closeDriverModal} size="lg">
                          <ModalHeader toggle={closeDriverModal}>Search Driver</ModalHeader>
                          <ModalBody>
                            <div className="d-flex gap-2 mb-3">
                              <Input
                                placeholder="Name"
                                value={driverSearchName}
                                onChange={e => setDriverSearchName(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), searchDriverList())}
                              />
                              <Input
                                placeholder="License Number"
                                value={driverSearchLicense}
                                onChange={e => setDriverSearchLicense(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), searchDriverList())}
                              />
                              <Input type="select" value={driverSearchActive} onChange={e => setDriverSearchActive(e.target.value)}>
                                <option value="">Any status</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                              </Input>
                            </div>
                            <div className="d-flex gap-2 mb-3">
                              <Button color="primary" type="button" onClick={searchDriverList} disabled={driversLoading}>
                                {driversLoading ? <Spinner size="sm" /> : 'Search'}
                              </Button>
                              <Button color="secondary" type="button" onClick={resetDriverSearch}>
                                Reset
                              </Button>
                            </div>
                            <div className="ntp-driver-results">
                              {driversLoading ? (
                                <div className="d-flex align-items-center gap-2 ntp-field-hint">
                                  <Spinner size="sm" />
                                  <span>Searching drivers...</span>
                                </div>
                              ) : drivers.length === 0 ? (
                                <div className="ntp-error">No drivers found. Try a different name, license number, or status.</div>
                              ) : (
                                drivers.map(driver => (
                                  <div key={driver.id || driver.driver_id} className="ntp-driver-row d-flex justify-content-between align-items-center p-3 mb-2 border rounded">
                                    <div>
                                      <div className="fw-bold">{driver.name}</div>
                                      <div className="text-muted">{driver.license_number || driver.drivingLicenseNumber}</div>
                                      <div className="text-muted">{driver.isActive ? 'Active' : 'Inactive'}</div>
                                    </div>
                                    <Button color="success" type="button" onClick={() => pickDriver(driver, setFieldValue)}>
                                      Select
                                    </Button>
                                  </div>
                                ))
                              )}
                            </div>
                          </ModalBody>
                          <ModalFooter>
                            <Button color="secondary" type="button" onClick={closeDriverModal}>
                              Close
                            </Button>
                          </ModalFooter>
                        </Modal>

                        <FormGroup className="mt-4 mb-0">
                          <Label className="ntp-field-label">Loading Capacity</Label>
                          <Input
                            type="number"
                            className="ntp-input"
                            placeholder="e.g. 10"
                            max={remainingQuantity || undefined}
                            value={values.loading_capacity}
                            onChange={e => setFieldValue('loading_capacity', e.target.value)}
                          />
                          {touched.loading_capacity && errors.loading_capacity && (
                            <div className="ntp-error mt-2">{errors.loading_capacity}</div>
                          )}
                          {!balanceLoading && remainingQuantity > 0 && (
                            <div className="ntp-field-hint mt-2">Maximum allowed: {remainingQuantity} {balanceQuantity?.quantity_unit || selectedRequest.quantity_unit || ''}</div>
                          )}
                        </FormGroup>

                        {selectedDriver && (
                          <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="ntp-select-card mt-4"
                          >
                            <Row className="g-3">
                              <Col md={6}>
                                <small className="ntp-mini-lbl d-block">Driver Name</small>
                                <span className="ntp-mini-val">{selectedDriver.name}</span>
                              </Col>
                              <Col md={6}>
                                <small className="ntp-mini-lbl d-block">License Number</small>
                                <span className="ntp-mini-val">{selectedDriver.license_number || selectedDriver.drivingLicenseNumber}</span>
                              </Col>
                              <Col md={6}>
                                <small className="ntp-mini-lbl d-block">Phone</small>
                                <span className="ntp-mini-val">{selectedDriver.phone || selectedDriver.mobile}</span>
                              </Col>
                            </Row>
                          </motion.div>
                        )}

                        <div className="ntp-queue-action">
                          <Button
                            type="button"
                            className="ntp-btn ntp-btn--secondary"
                            onClick={() => handleAddNomination(values, setFieldValue)}
                          >
                            <FaPlus className="me-2" /> Add Truck to Nomination List
                          </Button>
                        </div>

                        {queueError && <div className="ntp-error mt-3">{queueError}</div>}

                        {queuedNominations.length > 0 && (
                          <div className="ntp-queue-list mt-4">
                            <h6 className="ntp-queue-title">Nomination List ({queuedNominations.length})</h6>
                            {queuedNominations.map(item => (
                              <div className="ntp-queue-item" key={item.truck_id}>
                                <div>
                                  <span className="ntp-mini-lbl d-block">Truck</span>
                                  <span className="ntp-mini-val">{item.truck_number}</span>
                                </div>
                                <div>
                                  <span className="ntp-mini-lbl d-block">Driver</span>
                                  <span className="ntp-mini-val">{item.driver_name}</span>
                                </div>
                                <div>
                                  <span className="ntp-mini-lbl d-block">Loading Capacity</span>
                                  <span className="ntp-mini-val">{item.loading_capacity}</span>
                                </div>
                                <Button
                                  type="button"
                                  className="ntp-remove-btn"
                                  onClick={() => removeQueuedNomination(item.truck_id)}
                                >
                                  <FaTrash />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardBody>
                    </Card>

                    <div className="ntp-actions mb-5">
                      <Button 
                        className="ntp-btn ntp-btn--secondary px-5 py-2"
                        onClick={() => navigate(`/transport-request/${id}`)}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button 
                        className="ntp-btn ntp-btn--primary px-5 py-2"
                        type="submit" 
                        disabled={isSubmitting || (queuedNominations.length === 0 && !buildDraftNomination(values))}
                      >
                        {isSubmitting ? <Spinner size="sm" /> : <><FaCheck className="me-2" /> Confirm Nomination</>}
                      </Button>
                    </div>
                  </motion.div>
                </FormikForm>
              )}
            </Formik>
          </Col>

          <Col lg={4}>
            <motion.div variants={itemVariants}>
              <Card className="ntp-panel mb-4">
                <CardBody className="p-4">
                  <h5 className="ntp-section-title mb-4">Request Summary</h5>
                  <div className="ntp-summary-content">
                    <div className="ntp-summary-row">
                      <span className="ntp-summary-lbl">Origin</span>
                      <span className="ntp-summary-val">{selectedRequest.origin}</span>
                    </div>
                    <div className="ntp-summary-row">
                      <span className="ntp-summary-lbl">Destination</span>
                      <span className="ntp-summary-val">{selectedRequest.destination}</span>
                    </div>
                    <div className="ntp-summary-row">
                      <span className="ntp-summary-lbl">Weight</span>
                      <span className="ntp-summary-val">{selectedRequest.total_quantity} {selectedRequest.quantity_unit}</span>
                    </div>
                    <div className="ntp-summary-row">
                      <span className="ntp-summary-lbl">Date</span>
                      <span className="ntp-summary-val">{formatDate(selectedRequest.loading_date)}</span>
                    </div>
                    <div className="ntp-summary-row">
                      <span className="ntp-summary-lbl">Freight Payable By</span>
                      <span className="ntp-summary-val">{selectedRequest.freight_payable_by || '—'}</span>
                    </div>
                    {balanceQuantity?.request_status && (
                      <div className="ntp-summary-row">
                        <span className="ntp-summary-lbl">Balance Status</span>
                        <span className="ntp-summary-val">{balanceQuantity.request_status.replace(/_/g, ' ')}</span>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>

              <div className="ntp-warning p-3 rounded-3">
                <FaExclamationTriangle className="me-3" size={20} />
                <div>
                  <strong className="d-block mb-1">Important Note</strong>
                  <small>Nomination details are shared with the consignor immediately for vehicle entry and loading permissions.</small>
                </div>
              </div>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </motion.div>
  )
}

export default NominateTruckPage
